/**
 * Green screen in a browser: MediaPipe's person cutout (Brian's record.html,
 * Oct 18-22 2026, and his handoff's "Green screen on the computer", log #014).
 * Loaded only when Green screen is chosen, never bundled: the engine and models
 * ship in /mediapipe (so the app works offline) and the CDN is the fallback. In
 * the iPhone app the phone cuts you out itself (nativeCamera.ts) and none of
 * this loads.
 *
 * Two models:
 *   - On a computer, the selfie multiclass model (selfie_multiclass_256x256,
 *     16.4 MB): six classes (background, hair, body skin, face skin, clothes,
 *     others such as glasses and hats). You are every class but the background;
 *     the classes come out of a softmax, so that is exactly 1 minus the
 *     background's confidence, read from the one confidence mask (soft edges,
 *     one read back instead of five). Much steadier hair and hands.
 *   - Everywhere else, and as the fallback, the small selfie segmenter
 *     (selfie_segmenter, 250 KB).
 * The fallback rule: the big model is dropped for the small one when it fails to
 * load, or when it is too slow here: after 5 warm-up frames, the median of the
 * next 15 inferences over SLOW_MS (30 ms; the model runs on the main thread and
 * a 30 fps frame has 33 ms in all).
 *
 * The mask: the model's confidence, smoothed over frames with a blend that holds
 * still edges steady and lets real movement through (a big change passes almost
 * at once, a flicker is averaged away), choked in (3 pixels for the small model,
 * 1 for the multiclass one, whose hairline is worth keeping), then feathered;
 * the model runs at most 30 times a second.
 */

type Delegate = 'CPU' | 'GPU'
export type SegModel = 'multiclass' | 'selfie'
type Engine = [mjs: string, loader: string, wasm: string]

interface MPMask { width: number; height: number; getAsFloat32Array(): Float32Array; close?(): void }
interface MPResult { confidenceMasks?: MPMask[]; categoryMask?: MPMask; close?(): void }
interface MPSegmenter { segmentForVideo(src: CanvasImageSource, ts: number): MPResult; close?(): void }
interface MPVision {
  ImageSegmenter: {
    createFromOptions(fileset: { wasmLoaderPath: string; wasmBinaryPath: string }, opts: unknown): Promise<MPSegmenter>
  }
}

const MP_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/'
const MP_MODELS = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/'
const local = (u: string) => new URL(`/mediapipe/${u}`, location.href).href
const FILE: Record<SegModel, string> = { multiclass: 'selfie_multiclass_256x256.tflite', selfie: 'selfie_segmenter.tflite' }
/** Our own copy first, then the CDN; each engine with its own copy of the model. */
const ENGINES: { eng: () => Engine; model: (m: SegModel) => string }[] = [
  { eng: () => [local('vision_bundle.mjs'), local('vision_wasm_internal.js'), local('vision_wasm_internal.wasm')], model: (m) => local(FILE[m]) },
  { eng: () => [MP_CDN + 'vision_bundle.mjs', MP_CDN + 'wasm/vision_wasm_internal.js', MP_CDN + 'wasm/vision_wasm_internal.wasm'],
    model: (m) => MP_MODELS + (m === 'multiclass' ? 'selfie_multiclass_256x256/float32/latest/' : 'selfie_segmenter/float16/latest/') + FILE[m] },
]

// iPhone WebKit hands back blank masks from the GPU delegate: start on the CPU there.
const IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

/** The head the mask finds: the top of the mask, its width a little below; 0..1 of the mask. */
export interface Head { x: number; y: number; w: number }

export function headOf(isP: (i: number) => boolean, mw: number, mh: number): Head | null {
  const st = 2, need = Math.max(2, (mw / st) * 0.03)
  let top = -1
  for (let y = 0; y < mh && top < 0; y += st) {
    let n = 0
    for (let x = 0; x < mw; x += st) if (isP(y * mw + x)) n++
    if (n >= need) top = y
  }
  if (top < 0) return null
  let minX = mw, maxX = -1, rows = 0
  for (const k of [0.05, 0.08, 0.11]) {
    const y = Math.min(mh - 1, Math.round(top + k * mh))
    let l = -1, r = -1
    for (let x = 0; x < mw; x += st) if (isP(y * mw + x)) { if (l < 0) l = x; r = x }
    if (r >= 0) { rows++; if (l < minX) minX = l; if (r > maxX) maxX = r }
  }
  if (!rows || maxX <= minX) return null
  const w = (maxX - minX) / mw
  return { x: (minX + maxX) / 2 / mw, y: (top + (maxX - minX) * 0.62) / mh, w: Math.max(0.1, Math.min(0.6, w)) }
}

const SEG_MS = 33
/** The fallback rule: the multiclass model's median inference over this, after warm-up, drops to the small model. */
export const SLOW_MS = 30
const WARM = 5, TIMED = 15

export interface SegStats { model: SegModel | null; delegate: Delegate | null; loadMs: number; medianMs: number; lastMs: number; fellBack: '' | 'load' | 'slow'; slowMs?: number }

export class WebSegmenter {
  private seg: MPSegmenter | null = null
  private loading: Promise<boolean> | null = null
  private engine: (typeof ENGINES)[number] | null = null
  private delegates: Delegate[] = IOS ? ['CPU', 'GPU'] : ['GPU', 'CPU']
  private delegate: Delegate = this.delegates[0]
  private swapped = false
  private flat = 0
  private polarity = 0
  private ts = 0
  private last = 0
  private runs = 0
  private times: number[] = []
  private input: HTMLCanvasElement | null = null
  private alphaS: Float32Array | null = null
  private bufA: Float32Array | null = null
  private bufB: Float32Array | null = null
  model: SegModel | null = null
  stats: SegStats = { model: null, delegate: null, loadMs: 0, medianMs: 0, lastMs: 0, fellBack: '' }
  /** The current mask: white with the person's alpha. */
  mask: HTMLCanvasElement | null = null
  head: Head | null = null

  get ready() { return !!this.seg }

  /**
   * Loads once. big: try the multiclass model first (a computer). Resolves false
   * when nothing loads.
   */
  load(big = false): Promise<boolean> {
    if (this.seg) return Promise.resolve(true)
    if (this.loading) return this.loading
    this.loading = (async () => {
      const t0 = performance.now()
      for (const m of (big ? ['multiclass', 'selfie'] : ['selfie']) as SegModel[]) {
        for (const e of ENGINES) {
          try {
            this.seg = await this.create(e, m, this.delegate); this.engine = e; this.setModel(m)
            this.stats.loadMs = Math.round(performance.now() - t0)
            if (big && m === 'selfie') this.stats.fellBack = 'load'
            return true
          } catch (err) { console.warn('seg load', m, err) }
        }
      }
      return false
    })().finally(() => { this.loading = null })
    return this.loading
  }

  private setModel(m: SegModel) {
    this.model = m; this.stats.model = m; this.stats.delegate = this.delegate
    this.polarity = m === 'multiclass' ? 1 : 0; this.alphaS = null; this.flat = 0; this.runs = 0; this.times = []; this.stats.medianMs = 0
  }

  private async create(e: (typeof ENGINES)[number], m: SegModel, delegate: Delegate): Promise<MPSegmenter> {
    const [mjs, loader, wasm] = e.eng()
    const vision = (await import(/* @vite-ignore */ mjs)) as MPVision
    return vision.ImageSegmenter.createFromOptions({ wasmLoaderPath: loader, wasmBinaryPath: wasm }, {
      baseOptions: { modelAssetPath: e.model(m), delegate }, runningMode: 'VIDEO', outputCategoryMask: false, outputConfidenceMasks: true,
    })
  }

  /** Swaps the model or the delegate in place; the old one keeps the mask until the new one is up. */
  private async rebuild(m: SegModel, delegate: Delegate) {
    if (!this.engine) return
    const old = this.seg; this.seg = null
    try { old?.close?.() } catch { /* gone */ }
    try { this.delegate = delegate; this.seg = await this.create(this.engine, m, delegate); this.setModel(m) } catch (e) {
      console.warn('seg rebuild', m, delegate, e)
      if (m === 'multiclass') await this.rebuild('selfie', delegate)
    }
  }

  /** Runs the model on the picture when due (at most 30 a second; force runs it now). Returns the mask or null. */
  update(src: CanvasImageSource, vw: number, vh: number, force = false): HTMLCanvasElement | null {
    if (!this.seg) return null
    const now = performance.now()
    if (!force && this.mask && now - this.last < SEG_MS) return this.mask
    this.last = now
    if (!this.run(src, vw, vh) && !this.mask) return null
    return this.mask
  }

  private run(src: CanvasImageSource, vw: number, vh: number): boolean {
    if (!vw || !vh || !this.seg) return false
    // The models work at 256 inside anyway: feed a 256-wide copy.
    const iw = 256, ih = Math.max(2, Math.round((256 * vh) / vw / 2) * 2)
    if (!this.input || this.input.height !== ih) { this.input = document.createElement('canvas'); this.input.width = iw; this.input.height = ih }
    this.input.getContext('2d')!.drawImage(src, 0, 0, iw, ih)
    let r: MPResult
    const t0 = performance.now()
    try { r = this.seg.segmentForVideo(this.input, ++this.ts) } catch { return false }
    // the multiclass model's first mask is the background; the small model's only mask is you (its direction read once)
    const cm = r.confidenceMasks?.[0]
    if (!cm) { try { r.close?.() } catch { /* */ } return false }
    const conf = cm.getAsFloat32Array(), mw = cm.width, mh = cm.height
    this.timeIt(performance.now() - t0)
    let lo = 1, hi = 0
    for (let i = 0; i < conf.length; i += 11) { if (conf[i] < lo) lo = conf[i]; if (conf[i] > hi) hi = conf[i] }
    if (this.flat >= 0) {
      if (hi - lo < 0.2) {
        if (++this.flat >= 6 && !this.swapped && this.model) { this.flat = -1; this.swapped = true; void this.rebuild(this.model, this.delegates[1]) }
      } else this.flat = -1
    }
    if (!this.polarity) {
      // In a selfie the person fills the lower middle and the top corners are room.
      let mid = 0, nm = 0, cor = 0, nc = 0
      for (let yy = 0; yy < mh; yy += 4) for (let xx = 0; xx < mw; xx += 4) {
        const v = conf[yy * mw + xx], fx = xx / mw, fy = yy / mh
        if (fx > 0.35 && fx < 0.65 && fy > 0.35 && fy < 0.95) { mid += v; nm++ } else if (fy < 0.2 && (fx < 0.15 || fx > 0.85)) { cor += v; nc++ }
      }
      this.polarity = (nm ? mid / nm : 0) >= (nc ? cor / nc : 0) ? 1 : -1
    }
    const inv = this.model === 'multiclass' || this.polarity < 0
    const n = conf.length
    if (!this.alphaS || this.alphaS.length !== n) { this.alphaS = new Float32Array(n); for (let i = 0; i < n; i++) this.alphaS[i] = inv ? 1 - conf[i] : conf[i] }
    if (!this.bufA || this.bufA.length !== n) { this.bufA = new Float32Array(n); this.bufB = new Float32Array(n) }
    if (!this.mask || this.mask.width !== mw || this.mask.height !== mh) { this.mask = document.createElement('canvas'); this.mask.width = mw; this.mask.height = mh }
    const alphaS = this.alphaS, bufA = this.bufA, bufB = this.bufB!
    const mc = this.mask.getContext('2d')!, id = mc.createImageData(mw, mh), d = id.data
    // smoothing between frames: a still edge is held (0.75 of the last frame), a real move passes (0.15)
    for (let i = 0; i < n; i++) {
      const p = inv ? 1 - conf[i] : conf[i], s = alphaS[i], dd = p > s ? p - s : s - p
      const keep = 0.75 - 0.6 * (dd > 0.5 ? 1 : dd * 2)
      alphaS[i] = s * keep + p * (1 - keep)
    }
    // the choke: a minimum over 2R+1 pixels, across then down
    const R = this.model === 'multiclass' ? 1 : 3
    for (let y = 0; y < mh; y++) { const o = y * mw; for (let x = 0; x < mw; x++) { let m = 1; for (let k = -R; k <= R; k++) { const xx = x + k < 0 ? 0 : x + k >= mw ? mw - 1 : x + k; const v = alphaS[o + xx]; if (v < m) m = v } bufA[o + x] = m } }
    for (let x = 0; x < mw; x++) for (let y = 0; y < mh; y++) { let m = 1; for (let k = -R; k <= R; k++) { const yy = y + k < 0 ? 0 : y + k >= mh ? mh - 1 : y + k; const v = bufA[yy * mw + x]; if (v < m) m = v } bufB[y * mw + x] = m }
    // the feather: a 3 x 3 box, then a smoothstep
    for (let y = 0; y < mh; y++) { const o = y * mw; for (let x = 0; x < mw; x++) { const l = x > 0 ? x - 1 : 0, rr = x < mw - 1 ? x + 1 : mw - 1; bufA[o + x] = (bufB[o + l] + bufB[o + x] + bufB[o + rr]) / 3 } }
    // the small model's edge is pulled in a little more (as Brian had it); the multiclass one is centred on one half
    const off = this.model === 'multiclass' ? 0.5 : 0
    for (let i = 0; i < n; i++) {
      const y = (i / mw) | 0, u = y > 0 ? i - mw : i, dn = y < mh - 1 ? i + mw : i
      const a = (bufA[u] + bufA[i] + bufA[dn]) / 3, e = Math.min(1, Math.max(0, (a - 0.5) / 0.35 + off))
      const j = i * 4; d[j] = d[j + 1] = d[j + 2] = 255; d[j + 3] = Math.round(e * e * (3 - 2 * e) * 255)
    }
    mc.putImageData(id, 0, 0)
    this.head = headOf((i) => bufA[i] > 0.5, mw, mh) || this.head
    try { if (r.close) r.close(); else cm.close?.() } catch { /* */ }
    return true
  }

  /** Inference times; the multiclass model too slow here falls back to the small one. */
  private timeIt(ms: number) {
    this.stats.lastMs = Math.round(ms * 10) / 10
    if (++this.runs <= WARM) return
    if (this.times.length < TIMED) this.times.push(ms)
    if (this.times.length === TIMED && !this.stats.medianMs) {
      const t = [...this.times].sort((a, b) => a - b)
      this.stats.medianMs = Math.round(t[TIMED >> 1] * 10) / 10
      if (this.model === 'multiclass' && this.stats.medianMs > SLOW_MS) {
        console.info(`green screen: multiclass median ${this.stats.medianMs} ms > ${SLOW_MS} ms, using the small model`)
        this.stats.fellBack = 'slow'
        this.stats.slowMs = this.stats.medianMs
        void this.rebuild('selfie', this.delegate)
      }
    }
  }

  close() { try { this.seg?.close?.() } catch { /* */ } this.seg = null }
}

/** The native mask's alpha curve: Vision's half-sure ring stays soft but is pulled in a little. */
const NLUT = new Uint8Array(256)
for (let i = 0; i < 256; i++) { const e = Math.min(1, Math.max(0, (i / 255 - 0.3) / 0.5)); NLUT[i] = Math.round(e * e * (3 - 2 * e) * 255) }

/** Turns the native side's 8-bit mask into the same white-with-alpha canvas. */
export class NativeMask {
  canvas: HTMLCanvasElement | null = null
  private id: ImageData | null = null
  head: Head | null = null

  set(src: Uint8Array, mw: number, mh: number) {
    if (!this.canvas || this.canvas.width !== mw || this.canvas.height !== mh) {
      this.canvas = document.createElement('canvas'); this.canvas.width = mw; this.canvas.height = mh
      this.id = this.canvas.getContext('2d')!.createImageData(mw, mh); this.id.data.fill(255)
    }
    const d = this.id!.data
    for (let i = 0, j = 3; i < src.length; i++, j += 4) d[j] = NLUT[src[i]]
    this.head = headOf((i) => src[i] > 128, mw, mh) || this.head
    this.canvas.getContext('2d')!.putImageData(this.id!, 0, 0)
  }

  clear() { this.canvas = null }
}
